import { Trans } from '@lingui/macro'
import { createTheme, FormControl, FormControlLabel, FormControlProps, FormLabel, Grid, Checkbox, Radio, RadioGroup, Theme, ThemeProvider, useTheme } from '@mui/material'
import { Field } from 'formik'
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import React from 'react'
import { valueExistsTS } from 'utilsTS'
import { ErrorLabel } from '../forms/ErrorLabel'

export const getMuiTheme = (existingTheme: Theme) => createTheme({
  ...existingTheme,
  components: {
    ...existingTheme?.components,
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: existingTheme.palette.primary.main,
          fontWeight: 'bold',
          marginBottom: 16,
          '&.Mui-error': {
            color: existingTheme.palette.primary.main
          }
        }
      }
    },
  }
})

export const RadioGroupWithFormik = (
  props: FormControlProps & RadioGroupWithFormikProps
): JSX.Element => {
  const { name, options, label, compact, helperText, isCheckbox, renderInTwoColumns, row, onChange } = props

  const theme = useTheme()
  const _theme = getMuiTheme(theme)

  return (
    <Field name={name}>
      {({ field, form, meta }) => {
        const value = field.value ?? ''
        const displayError = Boolean(meta.touched as boolean && meta.error)

        return (
          <FormControl
            color={props.color}
            disabled={props.disabled}
            fullWidth={props.fullWidth}
            focused={props.focused}
            hiddenLabel={props.hiddenLabel}
            margin={props.margin}
            required={props.required}
            size={props.size}
            variant={props.variant}
            error={displayError}
            component='fieldset'
            onChange={undefined}
            sx={{ ...props.sx }}
          >
            {label && (
              <ThemeProvider theme={_theme}>
                <FormLabel>
                  {label}
                </FormLabel>
              </ThemeProvider>
            )}
            <RadioGroup
              row={row}
              style={valueExistsTS(renderInTwoColumns)
                ? {
                    flexDirection: 'row',
                    marginLeft: 25,
                    justifyContent: 'space-between'
                  }
                : {}}
              {...field}
              value={value}
              onChange={(e, v) => {
                field.onChange(e, v)
                if (onChange !== null && onChange !== undefined) {
                  onChange(e, v)
                }
              }}

            >
              {options.map(option => (
                <FormControlLabel
                  style={{
                    width: valueExistsTS(renderInTwoColumns) ? '45%' : undefined
                  }}
                  value={option.value}
                  key={option.value}
                  control={(
                    <Radio
                      {...(Boolean(isCheckbox) && {
                        icon: <CheckBoxOutlineBlankIcon />,
                        checkedIcon: <CheckBoxIcon />
                      })}
                      style={valueExistsTS(compact) ? { padding: 3 } : undefined}
                    />
                  )}
                  label={option.label}
                />
              ))}
            </RadioGroup>
            {Boolean(displayError) && (
              <Grid item xs>
                <ErrorLabel label={<Trans>{meta.error}</Trans>} />
              </Grid>
            )}
          </FormControl>
        )
      }}
    </Field>
  )
}

interface OptionProps {
  label: React.ReactNode
  value: string
}

interface RadioGroupWithFormikProps {
  /**
   * This is an id of Formiks field that the TextField is connected to
   */
  name: string
  /**
   * List of options to be rendered in the group
   */
  options: OptionProps[]
  /**
   * Form control label to render on the top of checkbox group.
   */
  label?: React.ReactNode
  /**
   * If true, lower padding will be used for chekboxes instead of default one
   */
  compact?: boolean
  /**
   * If true, checkboxes will be rendered in two columns
   */
  renderInTwoColumns?: boolean
  /**
   * Helper text to render below the checkbox group.
   */
  helperText?: React.ReactNode
  /**
   * If true, checkboxes will be rendered in a row
   */
  row?: boolean
  /**
   * If true, checkboxes will be rendered as checkboxes instead of radio buttons
   */
  isCheckbox?: boolean
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, value: string) => void
}
